import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { GitService } from '../../services/git.service';
import { AuthService } from '../../services/auth.service';

interface Repository {
  id: string;
  name: string;
  fullName: string;
  owner: string;
  avatarUrl: string;
  isPrivate: boolean;
  description: string;
  defaultBranch: string;
}

interface TerminalLog {
  time: string;
  text: string;
  type: 'info' | 'success' | 'error' | 'loading';
}

interface HistoryItem {
  date: string;
  time: string;
  preset: string;
  reposCount: number;
  branchesCount: number;
  status: 'Erfolgreich' | 'Fehlgeschlagen';
  size: string;
}

@Component({
  selector: 'app-git',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
    <div class="git-dashboard-wrapper">
      <!-- Top Navigation Header -->
      <header class="navbar-header glass">
        <div class="navbar-brand">
          <div class="git-logo-wrapper">
            <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="2.5" fill="none" class="git-icon-logo">
              <path d="M9 18V5l12-2v13"></path>
              <circle cx="6" cy="18" r="3"></circle>
              <circle cx="18" cy="16" r="3"></circle>
            </svg>
          </div>
          <span class="navbar-title">Sorrus Git Backup</span>
        </div>
        
        <div class="navbar-actions">
          <a routerLink="/launcher" class="nav-back-btn">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2.5" fill="none"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
            <span>Hub Launcher</span>
          </a>
          <div class="user-profile-badge" *ngIf="connected && githubUser">
            <img [src]="githubUser.avatarUrl" [alt]="githubUser.username" class="user-avatar">
            <span class="user-name">@{{ githubUser.username }}</span>
          </div>
        </div>
      </header>

      <main class="dashboard-content">
        <!-- 1. Connection View (If disconnected) -->
        <div class="connection-layout" *ngIf="!connected">
          <div class="connection-card glass">
            <div class="connection-hero">
              <div class="shield-orb">
                <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
              </div>
              <h2>GitHub-Verbindung einrichten</h2>
              <p>Sichern Sie Ihre Repositories und Organisationen verschlüsselt und automatisiert.</p>
            </div>

            <div class="connection-form">
              <div class="info-alert">
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none" class="alert-icon"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                <span>AES-256 Verschlüsselung schützt Ihre Zugangsdaten lokal auf dem Server.</span>
              </div>

              <div class="input-field-group">
                <label for="github-pat">Personal Access Token (PAT)</label>
                <div class="input-wrapper">
                  <input 
                    id="github-pat" 
                    type="password" 
                    [(ngModel)]="tokenInput" 
                    class="premium-input" 
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    (keydown.enter)="connect()"
                  >
                </div>
                <span class="field-hint">Erforderliche Berechtigungen: <code>repo</code> (für private Repos) und <code>read:org</code> (für Organisationen).</span>
              </div>

              <button class="action-btn primary" (click)="connect()" [disabled]="isConnecting || !tokenInput.trim()">
                <span *ngIf="!isConnecting">Verbindung herstellen</span>
                <span *ngIf="isConnecting" class="loader-inline">Verbinde...</span>
              </button>
            </div>

            <div class="help-section">
              <h4>Token erstellen:</h4>
              <ol>
                <li>GitHub Settings &gt; Developer Settings &gt; Personal Access Tokens &gt; Tokens (classic).</li>
                <li>Neuen Token generieren mit den Scopes <code>repo</code> und <code>read:org</code>.</li>
                <li>Kopieren Sie den Token und fügen Sie ihn hier ein.</li>
              </ol>
            </div>
          </div>
        </div>

        <!-- 2. Connected 3-Column Dashboard Layout -->
        <div class="dashboard-grid" *ngIf="connected">
          
          <!-- COLUMN 1: Control Panel, Terminal, History -->
          <section class="left-panel">
            
            <!-- Profile & Config Summary -->
            <div class="profile-summary-card glass">
              <div class="card-header-glow"></div>
              <div class="profile-info-row">
                <img [src]="githubUser?.avatarUrl" class="big-avatar" *ngIf="githubUser?.avatarUrl">
                <div class="profile-meta">
                  <h3>@{{ githubUser?.username }}</h3>
                  <span class="status-badge connected">Verbunden</span>
                </div>
                <button class="disconnect-btn" (click)="disconnectConfirm()" title="Verbindung trennen">
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                </button>
              </div>
              
              <div class="stats-row">
                <div class="stat-box">
                  <span class="stat-value">{{ getSelectedReposCount() }}</span>
                  <span class="stat-label">Repositories</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value">{{ getTotalSelectedCount() }}</span>
                  <span class="stat-label">Branches</span>
                </div>
                <div class="stat-box">
                  <span class="stat-value preset-name">{{ getSelectedPresetLabel() }}</span>
                  <span class="stat-label">Modus</span>
                </div>
              </div>
            </div>

            <!-- Start Backup & Terminal Feed -->
            <div class="terminal-card glass">
              <button class="start-backup-btn" (click)="startBackup()" [disabled]="isDownloading || getTotalSelectedCount() === 0">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2.5" fill="none" class="backup-icon" *ngIf="!isDownloading"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                <div class="spinner-inline" *ngIf="isDownloading"></div>
                <span>{{ isDownloading ? 'Backup läuft...' : 'Jetzt sichern (ZIP)' }}</span>
              </button>
              
              <div class="terminal-container">
                <div class="terminal-header">
                  <div class="terminal-dots">
                    <span class="dot red"></span>
                    <span class="dot yellow"></span>
                    <span class="dot green"></span>
                  </div>
                  <span class="terminal-title">backup-console.sh</span>
                </div>
                <div class="terminal-body" id="terminal-log-body">
                  <div class="terminal-placeholder" *ngIf="terminalLogs.length === 0">
                    <span class="prompt">$</span> Klicken Sie auf "Jetzt sichern (ZIP)", um den Backup-Prozess zu starten und die Konsole zu aktivieren.
                  </div>
                  <div class="terminal-line" *ngFor="let log of terminalLogs" [ngClass]="log.type">
                    <span class="log-time">[{{ log.time }}]</span>
                    <span class="log-text">{{ log.text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Backup History -->
            <div class="history-card glass">
              <div class="card-header">
                <h4>Verlauf der Backups</h4>
              </div>
              <div class="history-list">
                <div class="history-item" *ngFor="let item of backupHistory">
                  <div class="history-meta">
                    <span class="history-date">{{ item.date }} - {{ item.time }}</span>
                    <span class="history-preset">{{ item.preset }}</span>
                  </div>
                  <div class="history-details">
                    <span class="history-counts">{{ item.reposCount }} Repos / {{ item.branchesCount }} Branches</span>
                    <span class="history-size">{{ item.size }}</span>
                    <span class="history-status" [ngClass]="item.status === 'Erfolgreich' ? 'success' : 'failed'">{{ item.status }}</span>
                  </div>
                </div>
                <div class="history-empty" *ngIf="backupHistory.length === 0">
                  Noch keine Backups in dieser Sitzung durchgeführt.
                </div>
              </div>
            </div>

          </section>

          <!-- PANELS: 2-Column Selection Hub (Sidebar + Catalog Grid) -->
          <section class="right-panel">
            
            <!-- COLUMN 2: Organization Selector Sidebar -->
            <div class="org-sidebar glass">
              <div class="org-sidebar-header">
                <h4>Inhaber / Orgs</h4>
              </div>
              <div class="org-sidebar-list">
                <div 
                  class="org-sidebar-item" 
                  [class.active]="activeOwnerFilter === 'all'" 
                  (click)="selectOrganization('all')"
                >
                  <div class="org-avatar fallback-icon">★</div>
                  <div class="org-sidebar-info">
                    <span class="org-name-text">Alle Repositories</span>
                    <span class="org-selection-badge" *ngIf="getSelectedReposCount() > 0">
                      {{ getSelectedReposCount() }} Repos gewählt
                    </span>
                  </div>
                </div>

                <div 
                  class="org-sidebar-item" 
                  *ngFor="let owner of uniqueOwners"
                  [class.active]="activeOwnerFilter === owner" 
                  (click)="selectOrganization(owner)"
                >
                  <img [src]="getOrgAvatar(owner)" class="org-avatar" *ngIf="getOrgAvatar(owner)">
                  <div class="org-sidebar-info">
                    <span class="org-name-text">{{ owner }}</span>
                    <span class="org-selection-badge" *ngIf="getOrgSelectedStats(owner).repos > 0">
                      {{ getOrgSelectedStats(owner).repos }} Repos / {{ getOrgSelectedStats(owner).branches }} Branches
                    </span>
                  </div>
                  
                  <div class="org-quick-actions" (click)="$event.stopPropagation()">
                    <button class="org-quick-btn select" (click)="toggleAllInOrg(owner, true)" title="Alle wählen">✓</button>
                    <button class="org-quick-btn clear" (click)="toggleAllInOrg(owner, false)" title="Leeren">×</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- COLUMN 3: Repositories & Interactive Branches Grid -->
            <div class="repo-catalog-view">
              
              <!-- Catalog Header Row -->
              <div class="catalog-header-row">
                <div class="catalog-search-wrapper">
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2.5" fill="none" class="search-icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                  <input 
                    type="text" 
                    [(ngModel)]="searchTerm" 
                    class="catalog-search-input" 
                    placeholder="Repositories filtern..."
                  >
                </div>

                <div class="preset-toolbar" *ngIf="repos.length > 0">
                  <span class="toolbar-label">Schnell-Modus:</span>
                  <button class="preset-btn" [class.active]="selectedPreset === 'default-branch'" (click)="applyQuickPreset('default-branch')" title="Sichert nur Hauptbranches (z.B. main)">Standard-Branches</button>
                  <button class="preset-btn" [class.active]="selectedPreset === 'top-5-branches'" (click)="applyQuickPreset('top-5-branches')" title="Sichert die 5 aktuellsten Branches">Top-5 Branches</button>
                  <button class="preset-btn clear" (click)="clearAllSelections()" title="Alle Auswahlen aufheben">Alles leeren</button>
                </div>
              </div>

              <!-- Repository Catalog Container -->
              <div class="repos-grid-container" *ngIf="!isLoadingRepos">
                <div class="repos-grid">
                  
                  <!-- Repo Card -->
                  <div 
                    class="repo-card glass" 
                    *ngFor="let repo of filteredRepos"
                    [class.selected]="isRepoSelected(repo.id)"
                  >
                    <div class="repo-card-header">
                      <div class="repo-card-title-row">
                        <div class="repo-checkbox-wrapper" (click)="toggleRepoSelection(repo)">
                          <div class="check-box" [class.checked]="isRepoSelected(repo.id)">
                            <svg viewBox="0 0 24 24" width="10" height="10" stroke="currentColor" stroke-width="3.5" fill="none" *ngIf="isRepoSelected(repo.id)"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          </div>
                          <span class="repo-name">{{ repo.name }}</span>
                        </div>
                        <span class="visibility-badge" [class.private]="repo.isPrivate">
                          {{ repo.isPrivate ? 'Privat' : 'Öffentlich' }}
                        </span>
                      </div>
                      <p class="repo-description" *ngIf="repo.description" [title]="repo.description">
                        {{ repo.description }}
                      </p>
                    </div>

                    <div class="repo-card-body">
                      <div class="branches-header-row">
                        <span class="branches-title-label">Branches:</span>
                        <div class="branches-quick-actions" *ngIf="repoBranchesMap.has(repo.id)">
                          <button class="text-link-btn" (click)="toggleAllBranchesForRepo(repo, true)">Alle</button>
                          <span class="action-sep">|</span>
                          <button class="text-link-btn" (click)="toggleAllBranchesForRepo(repo, false)">Keine</button>
                        </div>
                      </div>

                       <!-- Interactive Branch List with Modification Dates -->
                      <div class="repo-branches-list" *ngIf="!isRepoLoadingBranches(repo.id) && repoBranchesMap.has(repo.id)">
                        <div 
                          class="branch-row" 
                          *ngFor="let branch of getRepoBranches(repo.id)" 
                          [class.active]="isBranchSelected(repo.id, branch.name)"
                          (click)="toggleBranch(repo, branch.name)"
                        >
                          <div class="branch-row-left">
                            <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" class="branch-icon">
                              <line x1="6" y1="3" x2="6" y2="15"></line>
                              <circle cx="18" cy="6" r="3"></circle>
                              <circle cx="6" cy="18" r="3"></circle>
                              <path d="M18 9a9 9 0 0 1-9 9"></path>
                            </svg>
                            <span class="branch-name" [title]="branch.name">{{ branch.name }}</span>
                          </div>
                          <div class="branch-row-right">
                            <span class="branch-date" *ngIf="branch.lastCommitDate">
                              {{ branch.lastCommitDate | date:'dd.MM.yyyy HH:mm' }}
                            </span>
                            <span class="branch-date-placeholder" *ngIf="!branch.lastCommitDate">-</span>
                          </div>
                        </div>
                        
                        <div class="empty-branches-message" *ngIf="getRepoBranches(repo.id).length === 0">
                          Keine Branches vorhanden.
                        </div>
                      </div>

                      <!-- Loading branches indicator -->
                      <div class="repo-branches-loading" *ngIf="isRepoLoadingBranches(repo.id)">
                        <div class="spinner-tiny"></div>
                        <span>Lade Branches...</span>
                      </div>

                      <!-- Show Branches manual trigger -->
                      <div class="repo-branches-load-trigger" *ngIf="!isRepoLoadingBranches(repo.id) && !repoBranchesMap.has(repo.id)">
                        <button class="load-branches-btn" (click)="loadBranchesForRepo(repo)">
                          Branches laden
                        </button>
                      </div>
                    </div>
                  </div>

                </div>

                <div class="catalog-empty" *ngIf="filteredRepos.length === 0">
                  <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" stroke-width="1.5" fill="none" class="empty-icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                  <p>Keine passenden Repositories gefunden.</p>
                </div>
              </div>

              <!-- General Loader -->
              <div class="catalog-loading" *ngIf="isLoadingRepos">
                <div class="spinner"></div>
                <p>Lade Repositories...</p>
              </div>

            </div>

          </section>

        </div>
      </main>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

    :host {
      font-family: 'Plus Jakarta Sans', sans-serif;
      display: block;
      min-height: 100vh;
      background-color: #080b11;
      color: #e2e8f0;
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
    }

    .git-dashboard-wrapper {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .glass {
      background: rgba(13, 18, 30, 0.7);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.04);
    }

    /* Navbar styling */
    .navbar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.15rem 2.5rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .navbar-brand {
      display: flex;
      align-items: center;
      gap: 0.85rem;
    }

    .git-logo-wrapper {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: linear-gradient(135deg, rgba(240, 80, 50, 0.15), rgba(240, 80, 50, 0.03));
      border: 1px solid rgba(240, 80, 50, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #f05032;
    }

    .git-icon-logo {
      filter: drop-shadow(0 2px 8px rgba(240, 80, 50, 0.3));
    }

    .navbar-title {
      font-size: 1.15rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: #ffffff;
    }

    .navbar-actions {
      display: flex;
      align-items: center;
      gap: 1.5rem;
    }

    .nav-back-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      color: #94a3b8;
      font-size: 0.875rem;
      font-weight: 600;
      padding: 0.5rem 0.85rem;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.04);
      transition: all 0.2s ease;
      background: rgba(255, 255, 255, 0.01);
    }

    .nav-back-btn:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .user-profile-badge {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      padding: 0.4rem 0.75rem;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.04);
      border-radius: 99px;
    }

    .user-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .user-name {
      font-size: 0.8rem;
      font-weight: 600;
      color: #cbd5e1;
    }

    /* Main layout */
    .dashboard-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    /* Connection view */
    .connection-layout {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4rem 2rem;
    }

    .connection-card {
      max-width: 540px;
      width: 100%;
      padding: 3rem;
      border-radius: 24px;
      display: flex;
      flex-direction: column;
      gap: 2rem;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
    }

    .connection-hero {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }

    .shield-orb {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: rgba(240, 80, 50, 0.08);
      color: #f05032;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0.5rem;
      border: 1px solid rgba(240, 80, 50, 0.15);
      box-shadow: 0 0 30px rgba(240, 80, 50, 0.1);
    }

    .connection-hero h2 {
      font-size: 1.65rem;
      font-weight: 800;
      color: #ffffff;
      margin: 0;
      letter-spacing: -0.02em;
    }

    .connection-hero p {
      color: #94a3b8;
      font-size: 0.95rem;
      line-height: 1.5;
      margin: 0;
    }

    .connection-form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .info-alert {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      padding: 0.85rem 1.15rem;
      background: rgba(16, 185, 129, 0.05);
      border: 1px solid rgba(16, 185, 129, 0.15);
      border-radius: 12px;
      color: #34d399;
      font-size: 0.825rem;
      font-weight: 500;
    }

    .alert-icon {
      flex-shrink: 0;
    }

    .input-field-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .input-field-group label {
      font-size: 0.85rem;
      font-weight: 600;
      color: #cbd5e1;
    }

    .premium-input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.85rem 1.15rem;
      border-radius: 12px;
      background: rgba(10, 15, 26, 0.85);
      border: 1px solid rgba(255, 255, 255, 0.06);
      color: #ffffff;
      font-size: 0.95rem;
      transition: all 0.2s ease;
      font-family: inherit;
    }

    .premium-input:focus {
      outline: none;
      border-color: #38bdf8;
      box-shadow: 0 0 15px rgba(56, 189, 248, 0.15);
      background: rgba(10, 15, 26, 0.95);
    }

    .field-hint {
      font-size: 0.75rem;
      color: #64748b;
      line-height: 1.4;
    }

    .field-hint code {
      background: rgba(255, 255, 255, 0.04);
      padding: 0.1rem 0.3rem;
      border-radius: 4px;
      color: #e2e8f0;
    }

    .action-btn {
      padding: 0.85rem 1.5rem;
      border-radius: 12px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      border: none;
      font-size: 0.95rem;
    }

    .action-btn.primary {
      background: linear-gradient(135deg, #0ea5e9, #4f46e5);
      color: #ffffff;
      box-shadow: 0 8px 24px -6px rgba(14, 165, 233, 0.4);
    }

    .action-btn.primary:hover:not([disabled]) {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px -4px rgba(14, 165, 233, 0.6);
      opacity: 0.95;
    }

    .action-btn.primary:active:not([disabled]) {
      transform: translateY(0);
    }

    .action-btn[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
      filter: grayscale(1);
    }

    .help-section {
      border-top: 1px solid rgba(255, 255, 255, 0.04);
      padding-top: 1.5rem;
    }

    .help-section h4 {
      font-size: 0.85rem;
      font-weight: 700;
      color: #94a3b8;
      margin: 0 0 0.75rem 0;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .help-section ol {
      margin: 0;
      padding-left: 1.25rem;
      color: #64748b;
      font-size: 0.8rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      line-height: 1.4;
    }

    /* Connected 3-column dashboard grid */
    .dashboard-grid {
      flex: 1;
      display: grid;
      grid-template-columns: 340px 1fr;
      gap: 1.5rem;
      padding: 1.5rem 2.5rem;
      height: calc(100vh - 80px);
      box-sizing: border-box;
      overflow: hidden;
    }

    .left-panel {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      overflow-y: auto;
      height: 100%;
      padding-right: 0.25rem;
    }

    .right-panel {
      display: grid;
      grid-template-columns: 260px 1fr;
      gap: 1.5rem;
      height: 100%;
      overflow: hidden;
    }

    /* Custom scrollbar in left panel */
    .left-panel::-webkit-scrollbar {
      width: 4px;
    }
    .left-panel::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 99px;
    }

    /* Profile summary card */
    .profile-summary-card {
      padding: 1.5rem;
      border-radius: 20px;
      position: relative;
      overflow: hidden;
    }

    .card-header-glow {
      position: absolute;
      top: -40px;
      left: -40px;
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: #0ea5e9;
      filter: blur(50px);
      opacity: 0.15;
      pointer-events: none;
    }

    .profile-info-row {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1.5rem;
      z-index: 10;
      position: relative;
    }

    .big-avatar {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .profile-meta {
      flex: 1;
    }

    .profile-meta h3 {
      font-size: 1.1rem;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 0.25rem 0;
      letter-spacing: -0.01em;
    }

    .status-badge {
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.15rem 0.5rem;
      border-radius: 6px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    .status-badge.connected {
      background: rgba(16, 185, 129, 0.12);
      color: #34d399;
      border: 1px solid rgba(16, 185, 129, 0.2);
    }

    .disconnect-btn {
      background: rgba(239, 68, 68, 0.05);
      border: 1px solid rgba(239, 68, 68, 0.15);
      color: #ef4444;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
    }

    .disconnect-btn:hover {
      background: #ef4444;
      color: #ffffff;
      box-shadow: 0 0 15px rgba(239, 68, 68, 0.35);
    }

    .stats-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.75rem;
      border-top: 1px solid rgba(255, 255, 255, 0.04);
      padding-top: 1.25rem;
    }

    .stat-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      background: rgba(255, 255, 255, 0.01);
      border: 1px solid rgba(255, 255, 255, 0.02);
      border-radius: 12px;
      padding: 0.75rem 0.5rem;
      text-align: center;
    }

    .stat-value {
      font-size: 1.25rem;
      font-weight: 800;
      color: #ffffff;
    }

    .stat-value.preset-name {
      font-size: 0.8rem;
      font-weight: 700;
      color: #38bdf8;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      box-sizing: border-box;
      padding: 0.35rem 0;
    }

    .stat-label {
      font-size: 0.675rem;
      color: #64748b;
      font-weight: 600;
      margin-top: 0.25rem;
    }

    /* Start Backup & Terminal */
    .terminal-card {
      padding: 1.25rem;
      border-radius: 20px;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .start-backup-btn {
      width: 100%;
      padding: 1rem;
      border-radius: 14px;
      background: linear-gradient(135deg, #f05032, #ff7a59);
      color: #ffffff;
      font-weight: 800;
      font-size: 1.05rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.65rem;
      border: none;
      box-shadow: 0 8px 24px -8px rgba(240, 80, 50, 0.4);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .start-backup-btn:hover:not([disabled]) {
      transform: translateY(-2px);
      box-shadow: 0 12px 30px -4px rgba(240, 80, 50, 0.6);
      opacity: 0.95;
    }

    .start-backup-btn:active:not([disabled]) {
      transform: translateY(0);
    }

    .start-backup-btn[disabled] {
      background: #334155;
      color: #64748b;
      cursor: not-allowed;
      box-shadow: none;
    }

    .backup-icon {
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
    }

    .terminal-container {
      background: #05070a;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 14px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: inset 0 2px 10px rgba(0,0,0,0.8);
    }

    .terminal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.65rem 1rem;
      background: #0a0e14;
      border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    }

    .terminal-dots {
      display: flex;
      gap: 0.35rem;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
    }
    .dot.red { background: #ef4444; }
    .dot.yellow { background: #f59e0b; }
    .dot.green { background: #10b981; }

    .terminal-title {
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.7rem;
      color: #475569;
      font-weight: 500;
    }

    .terminal-body {
      padding: 1rem;
      height: 180px;
      overflow-y: auto;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.725rem;
      line-height: 1.45;
      color: #94a3b8;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      box-sizing: border-box;
    }

    .terminal-body::-webkit-scrollbar {
      width: 4px;
    }
    .terminal-body::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.1);
      border-radius: 4px;
    }

    .terminal-placeholder {
      color: #475569;
      font-style: italic;
    }

    .terminal-placeholder .prompt {
      color: #f05032;
      font-weight: 700;
      font-style: normal;
    }

    .terminal-line {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      word-break: break-all;
    }

    .log-time {
      color: #3f5175;
      flex-shrink: 0;
    }

    .terminal-line.info { color: #e2e8f0; }
    .terminal-line.success { color: #34d399; }
    .terminal-line.error { color: #f87171; }
    .terminal-line.loading { color: #38bdf8; }

    /* Backup History Card */
    .history-card {
      padding: 1.25rem;
      border-radius: 20px;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .history-card .card-header h4 {
      margin: 0;
      font-size: 0.85rem;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .history-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .history-item {
      padding: 0.75rem;
      background: rgba(255, 255, 255, 0.01);
      border: 1px solid rgba(255,255,255,0.03);
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    .history-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .history-date {
      font-size: 0.725rem;
      font-weight: 600;
      color: #64748b;
    }

    .history-preset {
      font-size: 0.65rem;
      font-weight: 700;
      background: rgba(56, 189, 248, 0.08);
      border: 1px solid rgba(56, 189, 248, 0.15);
      color: #38bdf8;
      padding: 0.1rem 0.35rem;
      border-radius: 4px;
      text-transform: uppercase;
    }

    .history-details {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.775rem;
    }

    .history-counts {
      color: #cbd5e1;
      font-weight: 500;
    }

    .history-size {
      color: #94a3b8;
      font-family: monospace;
      font-weight: 600;
    }

    .history-status {
      font-size: 0.7rem;
      font-weight: 700;
    }
    .history-status.success { color: #10b981; }
    .history-status.failed { color: #ef4444; }

    .history-empty {
      padding: 1.5rem 0.5rem;
      text-align: center;
      color: #475569;
      font-size: 0.8rem;
      font-style: italic;
    }

    /* MIDDLE COLUMN: Org Sidebar */
    .org-sidebar {
      background: rgba(13, 18, 30, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.04);
      border-radius: 20px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      height: 100%;
    }

    .org-sidebar-header {
      padding: 1.25rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    }

    .org-sidebar-header h4 {
      margin: 0;
      font-size: 0.8rem;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .org-sidebar-list {
      flex: 1;
      overflow-y: auto;
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .org-sidebar-list::-webkit-scrollbar {
      width: 4px;
    }
    .org-sidebar-list::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }

    .org-sidebar-item {
      padding: 0.85rem;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.01);
      border: 1px solid rgba(255, 255, 255, 0.02);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      user-select: none;
    }

    .org-sidebar-item:hover {
      background: rgba(255, 255, 255, 0.03);
      border-color: rgba(255, 255, 255, 0.06);
    }

    .org-sidebar-item.active {
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(79, 70, 229, 0.03));
      border-color: rgba(14, 165, 233, 0.25);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .org-avatar {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      object-fit: cover;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .org-avatar.fallback-icon {
      background: rgba(56, 189, 248, 0.1);
      border-color: rgba(56, 189, 248, 0.2);
      color: #38bdf8;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.9rem;
      font-weight: bold;
    }

    .org-sidebar-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
    }

    .org-name-text {
      font-size: 0.85rem;
      font-weight: 700;
      color: #ffffff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .org-selection-badge {
      font-size: 0.675rem;
      color: #38bdf8;
      font-weight: 600;
    }

    .org-quick-actions {
      display: flex;
      gap: 0.3rem;
      opacity: 0.3;
      transition: opacity 0.2s;
    }

    .org-sidebar-item:hover .org-quick-actions,
    .org-sidebar-item.active .org-quick-actions {
      opacity: 1;
    }

    .org-quick-btn {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: 700;
      cursor: pointer;
      color: #ffffff;
      background: rgba(255, 255, 255, 0.05);
      transition: all 0.15s;
    }

    .org-quick-btn.select:hover {
      background: #10b981;
    }

    .org-quick-btn.clear:hover {
      background: #ef4444;
    }

    /* RIGHT COLUMN: Repository Catalog */
    .repo-catalog-view {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      height: 100%;
      overflow: hidden;
    }

    .catalog-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      background: rgba(13, 18, 30, 0.4);
      padding: 0.85rem 1.25rem;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.03);
    }

    .catalog-search-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      flex: 1;
    }

    .search-icon {
      position: absolute;
      left: 1rem;
      color: #475569;
    }

    .catalog-search-input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.65rem 1rem 0.65rem 2.65rem;
      border-radius: 10px;
      background: rgba(10, 15, 26, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.04);
      color: #ffffff;
      font-size: 0.85rem;
      transition: all 0.2s;
      font-family: inherit;
    }

    .catalog-search-input:focus {
      outline: none;
      border-color: #38bdf8;
      background: rgba(10, 15, 26, 0.75);
    }

    .preset-toolbar {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .toolbar-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      margin-right: 0.25rem;
    }

    .preset-btn {
      padding: 0.4rem 0.75rem;
      font-size: 0.75rem;
      font-weight: 700;
      border-radius: 8px;
      cursor: pointer;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.06);
      color: #e2e8f0;
      transition: all 0.15s;
    }

    .preset-btn:hover {
      background: rgba(56, 189, 248, 0.08);
      border-color: rgba(56, 189, 248, 0.2);
      color: #38bdf8;
    }

    .preset-btn.active {
      background: rgba(56, 189, 248, 0.15);
      border-color: rgba(56, 189, 248, 0.4);
      color: #38bdf8;
      box-shadow: 0 0 12px rgba(56, 189, 248, 0.15);
    }

    .preset-btn.clear {
      border-color: rgba(239, 68, 68, 0.2);
      color: #ff7a59;
    }

    .preset-btn.clear:hover {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.3);
      color: #ef4444;
    }

    /* Repo grid container */
    .repos-grid-container {
      flex: 1;
      overflow-y: auto;
      padding-right: 0.25rem;
    }

    .repos-grid-container::-webkit-scrollbar {
      width: 4px;
    }
    .repos-grid-container::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }

    .repos-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 1.25rem;
    }

    .repo-card {
      background: rgba(13, 18, 30, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.03);
      border-radius: 18px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .repo-card:hover {
      border-color: rgba(255, 255, 255, 0.08);
      background: rgba(13, 18, 30, 0.5);
      box-shadow: 0 10px 25px -12px rgba(0, 0, 0, 0.6);
      transform: translateY(-2px);
    }

    .repo-card.selected {
      border-color: rgba(14, 165, 233, 0.2);
      background: rgba(14, 165, 233, 0.015);
    }

    .repo-card-header {
      padding: 1.25rem 1.25rem 0.85rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.02);
    }

    .repo-card-title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.75rem;
    }

    .repo-checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
      flex: 1;
      min-width: 0;
    }

    .check-box {
      width: 18px;
      height: 18px;
      border-radius: 6px;
      border: 1px solid rgba(255,255,255,0.15);
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      transition: all 0.15s;
      flex-shrink: 0;
    }

    .check-box.checked {
      background: #0ea5e9;
      border-color: #0ea5e9;
      box-shadow: 0 0 10px rgba(14, 165, 233, 0.4);
    }

    .repo-name {
      font-size: 0.95rem;
      font-weight: 700;
      color: #ffffff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .visibility-badge {
      font-size: 0.65rem;
      font-weight: 700;
      padding: 0.15rem 0.45rem;
      border-radius: 5px;
      background: rgba(255, 255, 255, 0.05);
      color: #94a3b8;
      border: 1px solid rgba(255, 255, 255, 0.08);
      text-transform: uppercase;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }

    .visibility-badge.private {
      background: rgba(240, 80, 50, 0.08);
      border-color: rgba(240, 80, 50, 0.2);
      color: #ff7a59;
    }

    .repo-description {
      margin: 0.5rem 0 0;
      font-size: 0.775rem;
      color: #64748b;
      line-height: 1.45;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-height: 2.9em;
    }

    .repo-card-body {
      padding: 1rem 1.25rem 1.25rem;
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .branches-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .branches-title-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #475569;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    .branches-quick-actions {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }

    .text-link-btn {
      background: transparent;
      border: none;
      color: #38bdf8;
      font-size: 0.7rem;
      font-weight: 700;
      cursor: pointer;
      padding: 0.1rem 0.25rem;
      border-radius: 4px;
      transition: all 0.15s;
    }

    .text-link-btn:hover {
      background: rgba(56, 189, 248, 0.1);
      color: #0ea5e9;
    }

    .action-sep {
      color: rgba(255,255,255,0.06);
      font-size: 0.7rem;
    }

    .repo-branches-list {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      max-height: 180px;
      overflow-y: auto;
      padding-right: 0.25rem;
    }

    /* Custom scrollbar for branch list */
    .repo-branches-list::-webkit-scrollbar {
      width: 4px;
    }
    .repo-branches-list::-webkit-scrollbar-track {
      background: transparent;
    }
    .repo-branches-list::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.08);
      border-radius: 2px;
    }
    .repo-branches-list::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .branch-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.4rem 0.6rem;
      background: rgba(255, 255, 255, 0.015);
      border: 1px solid rgba(255, 255, 255, 0.03);
      border-radius: 8px;
      font-size: 0.75rem;
      color: #64748b;
      cursor: pointer;
      user-select: none;
      transition: all 0.18s ease;
    }

    .branch-row:hover {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(255, 255, 255, 0.08);
      color: #cbd5e1;
    }

    .branch-row.active {
      background: rgba(240, 80, 50, 0.08);
      border-color: rgba(240, 80, 50, 0.3);
      color: #ff7a59;
    }

    .repo-card.selected .branch-row.active {
      background: rgba(14, 165, 233, 0.08);
      border-color: rgba(14, 165, 233, 0.3);
      color: #38bdf8;
    }

    .branch-row-left {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      min-width: 0;
      flex: 1;
    }

    .branch-icon {
      flex-shrink: 0;
      opacity: 0.4;
      transition: opacity 0.15s;
    }
    .branch-row.active .branch-icon {
      opacity: 0.9;
    }

    .branch-name {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .branch-row-right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      padding-left: 0.5rem;
    }

    .branch-date {
      font-size: 0.7rem;
      color: #475569;
      font-weight: 500;
    }
    .branch-row.active .branch-date {
      color: inherit;
      opacity: 0.85;
    }

    .branch-date-placeholder {
      font-size: 0.7rem;
      color: #334155;
    }

    .repo-branches-loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.75rem;
      color: #64748b;
      padding: 0.5rem 0;
    }

    .spinner-tiny {
      width: 12px;
      height: 12px;
      border: 1.5px solid rgba(56, 189, 248, 0.1);
      border-top-color: #38bdf8;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    .repo-branches-load-trigger {
      display: flex;
      padding: 0.25rem 0;
    }

    .load-branches-btn {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.04);
      color: #cbd5e1;
      font-size: 0.725rem;
      font-weight: 700;
      padding: 0.35rem 0.75rem;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s;
    }

    .load-branches-btn:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }

    .empty-branches-message {
      font-size: 0.75rem;
      color: #475569;
      font-style: italic;
    }

    .catalog-empty, .catalog-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 5rem 2rem;
      text-align: center;
      color: #64748b;
      gap: 1rem;
    }

    .catalog-empty .empty-icon {
      color: #334155;
    }

    .catalog-empty p {
      font-size: 0.85rem;
      margin: 0;
    }

    .spinner {
      width: 36px;
      height: 36px;
      border: 3.5px solid rgba(56, 189, 248, 0.06);
      border-top-color: #38bdf8;
      border-radius: 50%;
      animation: spin 0.85s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 1000px) {
      .dashboard-grid {
        grid-template-columns: 1fr;
        height: auto;
        overflow: visible;
      }
      .right-panel {
        grid-template-columns: 1fr;
        height: auto;
        overflow: visible;
      }
    }
  `]
})
export class GitComponent implements OnInit {
  private gitService = inject(GitService);
  private authService = inject(AuthService);

  connected = false;
  githubUser: { username: string; avatarUrl: string } | null = null;
  tokenInput = '';
  isConnecting = false;

  repos: Repository[] = [];
  isLoadingRepos = false;
  searchTerm = '';
  activeOwnerFilter = 'all';

  // Preset configuration state (kept for macro shortcuts)
  selectedPreset: 'default-branch' | 'top-5-branches' | 'custom' = 'top-5-branches';

  // Selected branches mapping (repoId -> branches[])
  selectedBranchesMap = new Map<string, string[]>();
  isDownloading = false;

  // Repositories expanded branches caches
  repoBranchesMap = new Map<string, any[]>();
  repoLoadingBranchesMap = new Map<string, boolean>();

  // Live Terminal console logs
  terminalLogs: TerminalLog[] = [];
  
  // Backup Runs History (persistent in localStorage)
  backupHistory: HistoryItem[] = [];

  ngOnInit() {
    this.loadBackupHistory();
    this.checkConnection();
    
    // Restore preset preference
    const storedPreset = localStorage.getItem('git_backup_preset');
    if (storedPreset && ['default-branch', 'top-5-branches', 'custom'].includes(storedPreset)) {
      this.selectedPreset = storedPreset as any;
    } else {
      this.selectedPreset = 'top-5-branches';
      localStorage.setItem('git_backup_preset', 'top-5-branches');
    }
  }

  loadBackupHistory() {
    const history = localStorage.getItem('git_backup_history');
    if (history) {
      try {
        this.backupHistory = JSON.parse(history);
      } catch (e) {
        this.backupHistory = [];
      }
    }
  }

  saveBackupToHistory(status: 'Erfolgreich' | 'Fehlgeschlagen') {
    const historyItem: HistoryItem = {
      date: new Date().toLocaleDateString('de-DE'),
      time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      preset: this.getSelectedPresetLabel(),
      reposCount: this.getSelectedReposCount(),
      branchesCount: this.getTotalSelectedCount(),
      status: status,
      size: status === 'Erfolgreich' ? '~' + (this.getTotalSelectedCount() * 1.2).toFixed(1) + ' MB' : '0 MB'
    };
    
    this.backupHistory.unshift(historyItem);
    if (this.backupHistory.length > 5) {
      this.backupHistory.pop();
    }
    localStorage.setItem('git_backup_history', JSON.stringify(this.backupHistory));
  }

  checkConnection() {
    this.isLoadingRepos = true;
    this.gitService.getCredentials().subscribe({
      next: (res) => {
        if (res.connected) {
          this.connected = true;
          this.githubUser = {
            username: res.username,
            avatarUrl: res.avatarUrl
          };
          this.loadRepositories();
        } else {
          this.connected = false;
          this.isLoadingRepos = false;
        }
      },
      error: () => {
        this.connected = false;
        this.isLoadingRepos = false;
      }
    });
  }

  connect() {
    if (!this.tokenInput.trim()) return;
    this.isConnecting = true;

    this.gitService.saveCredentials(this.tokenInput.trim()).subscribe({
      next: (res) => {
        this.isConnecting = false;
        this.connected = true;
        this.githubUser = {
          username: res.username,
          avatarUrl: res.avatarUrl
        };
        this.tokenInput = '';
        this.loadRepositories();
      },
      error: (err) => {
        this.isConnecting = false;
        alert(err?.error?.message || 'Fehler beim Verbinden mit GitHub. Bitte prüfen Sie Ihren Token.');
      }
    });
  }

  disconnectConfirm() {
    if (confirm('Möchten Sie die Verbindung zu GitHub wirklich trennen? Ihre gespeicherten Zugangsdaten und Auswahlen werden gelöscht.')) {
      this.gitService.disconnect().subscribe({
        next: () => {
          this.connected = false;
          this.githubUser = null;
          this.repos = [];
          this.selectedBranchesMap.clear();
          this.repoBranchesMap.clear();
          this.repoLoadingBranchesMap.clear();
          this.terminalLogs = [];
        },
        error: () => {
          alert('Fehler beim Trennen der Verbindung.');
        }
      });
    }
  }

  loadRepositories() {
    this.isLoadingRepos = true;
    this.gitService.listRepositories().subscribe({
      next: (repos) => {
        this.repos = repos;
        this.isLoadingRepos = false;
        
        // Auto-select first organization if none active or active not in unique owners
        this.restoreSelection(() => {
          const owners = this.uniqueOwners;
          if (owners.length > 0) {
            if (this.activeOwnerFilter === 'all' || !owners.includes(this.activeOwnerFilter)) {
              this.selectOrganization(owners[0]);
            } else {
              this.autoLoadBranchesForActiveOrg();
            }
          }
        });
      },
      error: (err) => {
        this.isLoadingRepos = false;
        alert(err?.error?.message || 'Fehler beim Laden der Repositories.');
      }
    });
  }

  restoreSelection(callback?: () => void) {
    this.gitService.getSelection().subscribe({
      next: (selection) => {
        this.selectedBranchesMap.clear();
        if (Array.isArray(selection)) {
          for (const item of selection) {
            this.selectedBranchesMap.set(item.repoId, item.branches);
          }
        }
        if (callback) callback();
      },
      error: (err) => {
        console.error('Failed to restore selection', err);
        if (callback) callback();
      }
    });
  }

  get uniqueOwners(): string[] {
    const owners = new Set<string>();
    this.repos.forEach(r => owners.add(r.owner));
    return Array.from(owners).sort((a, b) => a.localeCompare(b));
  }

  get filteredRepos(): Repository[] {
    let result = this.repos;
    const term = this.searchTerm.trim().toLowerCase();

    if (this.activeOwnerFilter !== 'all') {
      result = result.filter(r => r.owner === this.activeOwnerFilter);
    }

    if (term) {
      result = result.filter(
        r => r.name.toLowerCase().includes(term) || r.owner.toLowerCase().includes(term)
      );
    }

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  getOrgAvatar(owner: string): string {
    const repo = this.repos.find(r => r.owner === owner);
    return repo ? repo.avatarUrl : '';
  }

  getOrgSelectedStats(owner: string): { repos: number, branches: number } {
    let selectedReposCount = 0;
    let selectedBranchesCount = 0;
    
    this.selectedBranchesMap.forEach((branches, repoId) => {
      const repo = this.repos.find(r => r.id === repoId);
      if (repo && repo.owner === owner) {
        selectedReposCount++;
        selectedBranchesCount += branches.length;
      }
    });
    
    return { repos: selectedReposCount, branches: selectedBranchesCount };
  }

  selectOrganization(owner: string) {
    this.activeOwnerFilter = owner;
    this.autoLoadBranchesForActiveOrg();
  }

  autoLoadBranchesForActiveOrg() {
    const repos = this.filteredRepos;
    if (repos.length > 15) {
      // Too many repos in current filter to autoload. We load on-demand.
      return;
    }
    const reposToLoad = repos.filter(r => !this.repoBranchesMap.has(r.id) && !this.isRepoLoadingBranches(r.id));
    
    let delay = 0;
    reposToLoad.forEach(repo => {
      setTimeout(() => {
        if (this.activeOwnerFilter === repo.owner || this.activeOwnerFilter === 'all') {
          this.loadBranchesForRepo(repo);
        }
      }, delay);
      delay += 250; // Throttle to prevent parallel request limit issues
    });
  }

  loadBranchesForRepo(repo: Repository, callback?: () => void) {
    if (this.repoLoadingBranchesMap.get(repo.id)) return;
    this.repoLoadingBranchesMap.set(repo.id, true);
    this.gitService.listBranches(repo.owner, repo.name).subscribe({
      next: (branches) => {
        const sorted = branches.sort((a, b) => {
          if (!a.lastCommitDate) return 1;
          if (!b.lastCommitDate) return -1;
          return new Date(b.lastCommitDate).getTime() - new Date(a.lastCommitDate).getTime();
        });
        this.repoBranchesMap.set(repo.id, sorted);
        this.repoLoadingBranchesMap.set(repo.id, false);
        if (callback) callback();
      },
      error: (err) => {
        console.error(err);
        this.repoLoadingBranchesMap.set(repo.id, false);
      }
    });
  }

  isRepoLoadingBranches(repoId: string): boolean {
    return !!this.repoLoadingBranchesMap.get(repoId);
  }

  getRepoBranches(repoId: string): any[] {
    return this.repoBranchesMap.get(repoId) || [];
  }

  isRepoSelected(repoId: string): boolean {
    return this.selectedBranchesMap.has(repoId);
  }

  toggleRepoSelection(repo: Repository) {
    const isSelected = this.selectedBranchesMap.has(repo.id);
    if (isSelected) {
      this.selectedBranchesMap.delete(repo.id);
      this.persistSelection();
    } else {
      const selectDefaultOnly = this.selectedPreset === 'default-branch';
      
      if (this.repoBranchesMap.has(repo.id)) {
        const branches = this.repoBranchesMap.get(repo.id) || [];
        const nextSelection = selectDefaultOnly 
          ? [repo.defaultBranch] 
          : branches.slice(0, 5).map(b => b.name);
        this.selectedBranchesMap.set(repo.id, nextSelection);
        this.persistSelection();
      } else {
        // Load branches first
        this.repoLoadingBranchesMap.set(repo.id, true);
        this.gitService.listBranches(repo.owner, repo.name).subscribe({
          next: (branches) => {
            const sorted = branches.sort((a, b) => {
              if (!a.lastCommitDate) return 1;
              if (!b.lastCommitDate) return -1;
              return new Date(b.lastCommitDate).getTime() - new Date(a.lastCommitDate).getTime();
            });
            this.repoBranchesMap.set(repo.id, sorted);
            
            const nextSelection = selectDefaultOnly 
              ? [repo.defaultBranch] 
              : sorted.slice(0, 5).map(b => b.name);
            this.selectedBranchesMap.set(repo.id, nextSelection);
            this.repoLoadingBranchesMap.set(repo.id, false);
            this.persistSelection();
          },
          error: () => {
            this.repoLoadingBranchesMap.set(repo.id, false);
            this.selectedBranchesMap.set(repo.id, [repo.defaultBranch]);
            this.persistSelection();
          }
        });
      }
    }
    
    // Set preset to custom on manual toggle
    this.selectedPreset = 'custom';
    localStorage.setItem('git_backup_preset', 'custom');
  }

  toggleBranch(repo: Repository, branchName: string) {
    const repoId = repo.id;
    const current = this.selectedBranchesMap.get(repoId) || [];
    
    let next: string[];
    if (current.includes(branchName)) {
      next = current.filter(b => b !== branchName);
    } else {
      next = [...current, branchName];
    }
    
    if (next.length === 0) {
      this.selectedBranchesMap.delete(repoId);
    } else {
      this.selectedBranchesMap.set(repoId, next);
    }
    
    this.selectedPreset = 'custom';
    localStorage.setItem('git_backup_preset', 'custom');
    this.persistSelection();
  }

  isBranchSelected(repoId: string, branchName: string): boolean {
    const selected = this.selectedBranchesMap.get(repoId) || [];
    return selected.includes(branchName);
  }

  toggleAllBranchesForRepo(repo: Repository, selectAll: boolean) {
    if (selectAll) {
      if (this.repoBranchesMap.has(repo.id)) {
        const branches = this.repoBranchesMap.get(repo.id) || [];
        this.selectedBranchesMap.set(repo.id, branches.map(b => b.name));
        this.persistSelection();
      } else {
        this.loadBranchesForRepo(repo, () => {
          const branches = this.repoBranchesMap.get(repo.id) || [];
          this.selectedBranchesMap.set(repo.id, branches.map(b => b.name));
          this.persistSelection();
        });
      }
    } else {
      this.selectedBranchesMap.delete(repo.id);
      this.persistSelection();
    }
    this.selectedPreset = 'custom';
    localStorage.setItem('git_backup_preset', 'custom');
  }

  toggleAllInOrg(owner: string, selectAll: boolean) {
    const orgRepos = this.repos.filter(r => r.owner === owner);
    
    if (selectAll) {
      const promises = orgRepos.map(repo => {
        if (this.selectedPreset === 'default-branch') {
          if (!this.selectedBranchesMap.has(repo.id)) {
            this.selectedBranchesMap.set(repo.id, [repo.defaultBranch]);
          }
          return Promise.resolve();
        } else {
          return this.fetchTop5BranchesForRepoPromise(repo);
        }
      });
      Promise.all(promises).then(() => {
        this.persistSelection();
      });
    } else {
      orgRepos.forEach(repo => {
        this.selectedBranchesMap.delete(repo.id);
      });
      this.persistSelection();
    }
  }

  applyQuickPreset(mode: 'default-branch' | 'top-5-branches') {
    this.selectedPreset = mode;
    localStorage.setItem('git_backup_preset', mode);
    
    const targetRepos = this.filteredRepos;
    
    if (mode === 'default-branch') {
      targetRepos.forEach(repo => {
        this.selectedBranchesMap.set(repo.id, [repo.defaultBranch]);
      });
      this.persistSelection();
    } else {
      // Load top 5 branches for all repos in the active view and select them
      const promises = targetRepos.map(repo => {
        return this.fetchTop5BranchesForRepoPromise(repo);
      });
      Promise.all(promises).then(() => {
        this.persistSelection();
      });
    }
  }

  clearAllSelections() {
    this.selectedBranchesMap.clear();
    this.persistSelection();
    this.selectedPreset = 'custom';
    localStorage.setItem('git_backup_preset', 'custom');
  }

  fetchTop5BranchesForRepoPromise(repo: Repository): Promise<void> {
    if (this.repoBranchesMap.has(repo.id)) {
      const branches = this.repoBranchesMap.get(repo.id) || [];
      this.selectedBranchesMap.set(repo.id, branches.slice(0, 5).map(b => b.name));
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.repoLoadingBranchesMap.set(repo.id, true);
      this.gitService.listBranches(repo.owner, repo.name).subscribe({
        next: (branches) => {
          const sorted = branches.sort((a, b) => {
            if (!a.lastCommitDate) return 1;
            if (!b.lastCommitDate) return -1;
            return new Date(b.lastCommitDate).getTime() - new Date(a.lastCommitDate).getTime();
          });
          this.repoBranchesMap.set(repo.id, sorted);
          this.selectedBranchesMap.set(repo.id, sorted.slice(0, 5).map(b => b.name));
          this.repoLoadingBranchesMap.set(repo.id, false);
          resolve();
        },
        error: () => {
          this.repoLoadingBranchesMap.set(repo.id, false);
          this.selectedBranchesMap.set(repo.id, [repo.defaultBranch]);
          resolve();
        }
      });
    });
  }

  getSelectedBranchesCount(repoId: string): number {
    return (this.selectedBranchesMap.get(repoId) || []).length;
  }

  getSelectedReposCount(): number {
    return this.selectedBranchesMap.size;
  }

  getTotalSelectedCount(): number {
    let count = 0;
    this.selectedBranchesMap.forEach((branches) => {
      count += branches.length;
    });
    return count;
  }

  getSelectedPresetLabel(): string {
    switch (this.selectedPreset) {
      case 'default-branch': return 'Standard-Branch';
      case 'top-5-branches': return 'Top-5 Branches';
      case 'custom': return 'Manuell';
    }
  }

  persistSelection() {
    const selection = this.getSelectionArray();
    this.gitService.saveSelection(selection).subscribe({
      error: (err) => {
        console.error('Failed to save selection on server', err);
      }
    });
  }

  getSelectionArray(): any[] {
    const array: any[] = [];
    this.selectedBranchesMap.forEach((branches, repoId) => {
      const repo = this.repos.find((r) => r.id === repoId);
      if (repo) {
        array.push({
          repoId: repo.id,
          repoName: repo.name,
          owner: repo.owner,
          branches: branches
        });
      }
    });
    return array;
  }

  // Interactive Terminal log helper
  addLog(text: string, type: 'info' | 'success' | 'error' | 'loading' = 'info') {
    const time = new Date().toLocaleTimeString('de-DE');
    this.terminalLogs.push({ time, text, type });
    // Auto-scroll log body
    setTimeout(() => {
      const el = document.getElementById('terminal-log-body');
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 40);
  }

  startBackup() {
    if (this.getTotalSelectedCount() === 0) return;
    this.isDownloading = true;
    this.terminalLogs = [];

    this.addLog('Initializing backup operation...', 'info');
    setTimeout(() => this.addLog('Connecting to GitHub API v3...', 'loading'), 300);
    setTimeout(() => this.addLog(`Authenticated as @${this.githubUser?.username}. Token check: OK`, 'success'), 700);
    setTimeout(() => this.addLog(`Applying preset rules: ${this.selectedPreset.toUpperCase()}`, 'info'), 1100);

    const selection = this.getSelectionArray();
    let delay = 1500;

    // Log each repo in queue
    selection.forEach((item) => {
      setTimeout(() => {
        this.addLog(`Processing repository: ${item.owner}/${item.repoName}...`, 'loading');
      }, delay);
      delay += 400;

      item.branches.forEach((b: string) => {
        setTimeout(() => {
          this.addLog(`-> Archiving branch [${b}]...`, 'loading');
        }, delay);
        delay += 500;
        setTimeout(() => {
          this.addLog(`-> Branch [${b}] compressed.`, 'success');
        }, delay);
        delay += 200;
      });
    });

    setTimeout(() => {
      this.addLog('Compiling archives into monolithic ZIP package...', 'loading');
    }, delay);
    delay += 700;

    setTimeout(() => {
      this.addLog('Streaming ZIP download to client browser...', 'info');
      
      const token = this.authService.getGuid() || '';
      const email = this.authService.getUserEmail() || '';
      const code = this.authService.get2faCode() || '';
      
      let url = `/api/git/backup?x-api-token=${encodeURIComponent(token)}`;
      if (email) {
        url += `&x-user-email=${encodeURIComponent(email)}`;
      }
      if (code) {
        url += `&x-2fa-code=${encodeURIComponent(code)}`;
      }
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sorrus_Git_Backup_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      this.addLog('ZIP download started in browser.', 'success');
      this.addLog('Backup process finished successfully. Status: 100% COMPLETE', 'success');
      this.isDownloading = false;
      this.saveBackupToHistory('Erfolgreich');
    }, delay);
  }
}
